// Columna central del explorer (`.col-table` del mockup): toolbar con las cuatro métricas y la
// paginación, y la tabla compacta de siete columnas.
//
// Las métricas se calculan sobre el UNIVERSO, no sobre lo filtrado: son la foto de la agenda del
// vendedor, y filtrar por «Perdidos» dejaría la conversión en 0%.
import { ChevronDown, ChevronLeft, ChevronRight, ChevronUp } from 'lucide-react';
import { Button, ErrorState, Skeleton } from '@/kit';
import { cn } from '@/lib/cn';
import { formatShortDateMx } from '@/lib/dateHelpers';
import { prospectReasonText } from '@/domains/sales/wizard/prospects';
import { prospectsErrorCopy } from '../lib/prospectsErrors';
import {
    nextSort,
    PAGE_SIZE,
    type ProspectMetrics,
    type SortKey,
    type SortState,
} from '../lib/prospectsFilters';
import {
    createdIso,
    nextContactCell,
    pluralProspects,
    shortFolio,
    statusChipClass,
    statusLabel,
    PENDING_CELL,
} from '../lib/prospectsFormat';
import type { ProspectRow } from '../lib/prospectsRows';

interface Column {
    key: SortKey;
    label: string;
    width?: string;
}

/**
 * Los anchos de las columnas de dato fijo (fecha, folio) son literales; las de texto variable llevan
 * un piso en px más un porcentaje, así crecen cuando la pantalla da y se recortan solo si no cabe.
 *
 * El piso es lo que hacía falta para que las 7 quepan en la columna central del explorer con la
 * ficha abierta: con los anchos del mockup (686px) las cuatro últimas caían fuera de la vista.
 */
const COLUMNS: Column[] = [
    { key: 'createdAt', label: 'Creado', width: 'w-[62px]' },
    { key: 'folio', label: 'Folio', width: 'w-[64px]' },
    { key: 'customerName', label: 'Cliente' },
    { key: 'product', label: 'Producto interés', width: 'w-[18%] min-w-[104px]' },
    { key: 'reason', label: 'Motivo', width: 'w-[18%] min-w-[96px]' },
    { key: 'nextContactAt', label: 'Próx. contacto', width: 'w-[76px]' },
    { key: 'status', label: 'Estado', width: 'w-[12%] min-w-[74px]' },
];

const CELL = 'truncate border-b border-border/60 px-1.5 py-1 text-[11.5px]';

const CHIP =
    'inline-flex items-center rounded-full border px-1.5 py-0.5 text-[9.5px] font-semibold';

const NEXT_CONTACT_TONE = {
    overdue: 'font-semibold text-destructive',
    today: 'font-semibold text-primary',
    plain: 'text-foreground',
};

export function ProspectsTable({
    rows,
    metrics,
    hasList,
    today,
    page,
    onPageChange,
    sort,
    onSortChange,
    selectedId,
    onSelect,
    isLoading,
    isTruncated,
    error,
    onRetry,
    hasFilters,
    onClearFilters,
}: {
    /** Filas YA filtradas y ordenadas — la paginación se aplica aquí adentro. */
    rows: ProspectRow[];
    metrics: ProspectMetrics;
    /** Si la agenda no se leyó, las métricas valen cero por ausencia de datos y no se afirman. */
    hasList: boolean;
    today: string;
    page: number;
    onPageChange: (page: number) => void;
    sort: SortState;
    onSortChange: (next: SortState) => void;
    selectedId: string | null;
    onSelect: (prospectId: string) => void;
    isLoading: boolean;
    /** Se topó el techo de páginas y el silo seguía ofreciendo: la agenda está incompleta. */
    isTruncated: boolean;
    error: Error | null;
    onRetry: () => void;
    /** Con filtros puestos, el vacío se resuelve limpiándolos; sin ellos, la agenda está vacía. */
    hasFilters: boolean;
    onClearFilters: () => void;
}) {
    const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
    const safePage = Math.min(page, totalPages);
    const pageRows = rows.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
    const copy = error ? prospectsErrorCopy(error) : null;

    return (
        <>
            {/* .table-toolbar */}
            <div className="flex min-h-10 flex-none flex-wrap items-center gap-2 border-b border-border px-3 py-1.5 text-[11px]">
                <div className="text-muted-foreground">
                    {isLoading || !hasList
                        ? `${PENDING_CELL} prospectos`
                        : pluralProspects(rows.length)}
                </div>
                <Metric
                    label="Vencidos"
                    value={hasList ? metrics.overdue : PENDING_CELL}
                    tone="text-destructive"
                />
                <Metric
                    label="Hoy"
                    value={hasList ? metrics.today : PENDING_CELL}
                    tone="text-primary"
                />
                <Metric
                    label="Conversión"
                    value={hasList ? `${String(metrics.conversionPct)}%` : PENDING_CELL}
                    tone="text-success"
                />
                <Metric label="Perdidos" value={hasList ? metrics.lost : PENDING_CELL} last />
                <div className="flex-1" />
                <div className="flex items-center gap-1">
                    <button
                        type="button"
                        aria-label="Página anterior"
                        disabled={safePage <= 1}
                        onClick={() => {
                            onPageChange(safePage - 1);
                        }}
                        className="flex size-[22px] items-center justify-center rounded border border-border bg-card transition-colors hover:bg-accent disabled:cursor-not-allowed disabled:opacity-40"
                    >
                        <ChevronLeft className="size-3" aria-hidden />
                    </button>
                    <span className="tabular-nums">
                        {safePage} / {totalPages}
                    </span>
                    <button
                        type="button"
                        aria-label="Página siguiente"
                        disabled={safePage >= totalPages}
                        onClick={() => {
                            onPageChange(safePage + 1);
                        }}
                        className="flex size-[22px] items-center justify-center rounded border border-border bg-card transition-colors hover:bg-accent disabled:cursor-not-allowed disabled:opacity-40"
                    >
                        <ChevronRight className="size-3" aria-hidden />
                    </button>
                </div>
            </div>

            {isTruncated ? (
                <div className="flex-none border-b border-border bg-warning-soft px-3 py-1.5 text-[10.5px] text-warning">
                    Hay más prospectos de los que caben en una vista y no se alcanzaron a cargar
                    todos. Acota con los filtros para ver el resto.
                </div>
            ) : null}

            <div className="min-h-0 flex-1 overflow-auto">
                {copy ? (
                    <ErrorState
                        className="m-3"
                        title={copy.title}
                        description={copy.description}
                        onRetry={copy.canRetry ? onRetry : undefined}
                    />
                ) : (
                    // min-w = los 476px de anchos fijos + 90 para el cliente. Sin este piso, con
                    // `table-fixed` la columna sin ancho colapsa a 0.
                    <table className="w-full min-w-[566px] table-fixed">
                        <thead className="sticky top-0 z-10 bg-card">
                            <tr className="border-b border-border">
                                {COLUMNS.map((col) => (
                                    <th
                                        key={col.key}
                                        scope="col"
                                        className={cn(
                                            'px-1.5 py-1.5 text-left text-[9px] font-semibold uppercase tracking-[0.03em] text-muted-foreground',
                                            col.width,
                                        )}
                                    >
                                        <SortButton
                                            label={col.label}
                                            sortKey={col.key}
                                            sort={sort}
                                            onSortChange={onSortChange}
                                        />
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {isLoading
                                ? Array.from({ length: 10 }, (_unused, i) => (
                                      <tr key={i}>
                                          {COLUMNS.map((col) => (
                                              <td key={col.key} className={CELL}>
                                                  <Skeleton className="h-3" />
                                              </td>
                                          ))}
                                      </tr>
                                  ))
                                : pageRows.map((row) => (
                                      <ProspectTableRow
                                          key={row.folio}
                                          row={row}
                                          today={today}
                                          selected={row.folio === selectedId}
                                          onSelect={onSelect}
                                      />
                                  ))}
                        </tbody>
                    </table>
                )}
                {!isLoading && !error && rows.length === 0 ? (
                    <div className="flex flex-col items-center gap-2 py-8 text-center text-[12px] text-muted-foreground">
                        {hasFilters ? (
                            <>
                                <span>Ningún prospecto coincide con los filtros.</span>
                                <Button variant="outline" size="sm" onClick={onClearFilters}>
                                    Quitar los filtros
                                </Button>
                            </>
                        ) : (
                            <span>
                                Cuando guardes un prospecto desde una venta, aparecerá aquí.
                            </span>
                        )}
                    </div>
                ) : null}
            </div>
        </>
    );
}

/** `.stat-metric` del mockup: rótulo chico y valor, separados por un borde vertical. */
function Metric({
    label,
    value,
    tone,
    last,
}: {
    label: string;
    value: number | string;
    tone?: string;
    last?: boolean;
}) {
    return (
        <span
            className={cn(
                'inline-flex items-baseline gap-1 px-2',
                !last && 'border-r border-border',
            )}
        >
            <span className="text-[9.5px] font-semibold uppercase tracking-[0.04em] text-muted-foreground">
                {label}
            </span>
            <span className={cn('text-[12px] font-semibold tabular-nums', tone)}>{value}</span>
        </span>
    );
}

function SortButton({
    label,
    sortKey,
    sort,
    onSortChange,
}: {
    label: string;
    sortKey: SortKey;
    sort: SortState;
    onSortChange: (next: SortState) => void;
}) {
    const active = sort.key === sortKey;
    return (
        <button
            type="button"
            onClick={() => {
                onSortChange(nextSort(sort, sortKey));
            }}
            className={cn(
                'inline-flex w-full max-w-full select-none items-center gap-0.5 transition-colors hover:text-foreground',
                active && 'text-foreground',
            )}
        >
            <span className="truncate">{label}</span>
            {active && sort.direction === 'asc' ? (
                <ChevronUp className="size-2.5" aria-hidden />
            ) : (
                <ChevronDown className={cn('size-2.5', !active && 'opacity-40')} aria-hidden />
            )}
        </button>
    );
}

function ProspectTableRow({
    row,
    today,
    selected,
    onSelect,
}: {
    row: ProspectRow;
    today: string;
    selected: boolean;
    onSelect: (prospectId: string) => void;
}) {
    const prospect = row.prospect;
    const reason = prospectReasonText(prospect);
    const contact = nextContactCell(prospect.nextContactAt, prospect.status, today);
    return (
        <tr
            onClick={() => {
                onSelect(row.folio);
            }}
            aria-selected={selected}
            className={cn(
                'cursor-pointer',
                selected
                    ? 'bg-primary/[0.08] shadow-[inset_2px_0_0_hsl(var(--primary))] hover:bg-primary/[0.12]'
                    : 'hover:bg-accent/40',
            )}
        >
            <td className={cn(CELL, 'tabular-nums text-muted-foreground')}>
                {formatShortDateMx(createdIso(prospect))}
            </td>
            <td
                className={cn(CELL, 'font-mono text-[10.5px] text-muted-foreground')}
                title={row.folio}
            >
                {shortFolio(row.folio)}
            </td>
            <td className={cn(CELL, 'font-medium')} title={row.customerName}>
                {row.customerName}
            </td>
            <td className={cn(CELL, 'text-[11px]')} title={row.productLabel}>
                {row.productLabel}
            </td>
            <td className={cn(CELL, 'overflow-visible')}>
                <span
                    title={reason}
                    className={cn(CHIP, 'max-w-full truncate border-warning text-warning')}
                >
                    {reason}
                </span>
            </td>
            <td className={cn(CELL, NEXT_CONTACT_TONE[contact.tone])}>{contact.text}</td>
            <td className={cn(CELL, 'overflow-visible')}>
                <span className={cn(CHIP, statusChipClass(prospect.status))}>
                    {statusLabel(prospect.status)}
                </span>
            </td>
        </tr>
    );
}
